import { useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Creative } from '@/api/types';
import type { AssetAnimation, AttachCreativeAssetRequest } from '@/api/types.gen';
import { ApiError } from '@/api/errors';
import { useSaveCreativeContent } from '@/api/manage-hooks';
import { creativeById, creativesList } from '@/api/resources';
import { uploadCreativeFile, type LocalCreativeAsset } from '@/api/creative-upload';
import { holdRelease } from '@/lib/release-hold';
import {
  CHANNEL_FIELDS,
  isCreativeChannel,
  isValidCreativeDestinationUrl,
  metaCopyIssues,
  metaFieldsFromCreative,
  serializeMetaCreativeSpec,
  type CreativeField,
} from '@/screens/creative-config';
import { CreativeFieldRow } from '@/screens/creative-field-row';
import { placementLabel } from '@/lib/creative-placement-labels';
import { DISPLAY_ACCEPT, validateDisplayFile } from '@/lib/media-validation/display';
import { DisplayAnimationNote } from './display-animation-note';
import { ResponsiveOverlay } from './responsive-overlay';

export interface ContentIssue {
  key: string;
  message: string;
}
const fieldsFor = (channel: string): readonly CreativeField[] =>
  isCreativeChannel(channel) ? CHANNEL_FIELDS[channel] : [];
const PLACEHOLDER_PREFIX = 'dsp_new_';
export const PLACEHOLDER_HINT = 'Choose a file for the new display placement, or remove it.';

/**
 * What `PUT /creatives/{id}/content` refuses, asked before the request leaves:
 * a blank post text, headline or CTA, an unknown CTA, a bad display link
 * (`metaCopyIssues`, the same gate the drawer's PATCH path uses), copy past
 * the API's hard limit, and a destination that is not an HTTPS URL. The UI
 * refuses first, with the same reason, or the server's 422 lands on a form
 * with no field to point at.
 */
export function creativeContentIssues(
  channel: string,
  fields: Record<string, string>,
): ContentIssue[] {
  const issues: ContentIssue[] =
    channel === 'meta' ? (metaCopyIssues(fields) as ContentIssue[]) : [];
  for (const field of fieldsFor(channel)) {
    if (field.hardMax && (fields[field.key] ?? '').length > field.hardMax) {
      issues.push({
        key: field.key,
        message: `Keep ${field.label.toLowerCase()} within ${field.hardMax} characters.`,
      });
    }
  }
  if (!isValidCreativeDestinationUrl((fields.dest_url ?? '').trim())) {
    issues.push({ key: 'dest_url', message: 'Enter a valid HTTPS destination URL.' });
  }
  return issues;
}

/** The size a Display placement already has, from its tag (`dsp_300x250_1`). */
function displayTagSize(tag: string): { w: number; h: number } | null {
  const match = /^dsp_(\d+)x(\d+)_/.exec(tag);
  return match ? { w: Number(match[1]), h: Number(match[2]) } : null;
}

async function probe(file: File): Promise<LocalCreativeAsset> {
  const url = URL.createObjectURL(file);
  try {
    const video = file.type.startsWith('video/');
    const el = video ? document.createElement('video') : new Image();
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error('The file metadata could not be read.')),
        15000,
      );
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      el.onerror = () => {
        clearTimeout(timer);
        reject(new Error('The file could not be read.'));
      };
      if (el instanceof HTMLVideoElement) {
        el.preload = 'metadata';
        el.onloadedmetadata = done;
      } else el.onload = done;
      el.src = url;
    });
    return {
      file,
      name: file.name,
      type: video ? 'video' : 'image',
      url,
      width: el instanceof HTMLVideoElement ? el.videoWidth : el.naturalWidth,
      height: el instanceof HTMLVideoElement ? el.videoHeight : el.naturalHeight,
      durationMs: el instanceof HTMLVideoElement ? Math.round(el.duration * 1000) : undefined,
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/** Owns an editing snapshot; query refreshes never replace entered values. */
export function CreativeContentEditor({
  creative,
  onClose,
  onSaved,
}: {
  creative: Creative;
  onClose: () => void;
  onSaved: (creative: Creative) => void;
}) {
  const [base] = useState(creative);
  const [fields, setFields] = useState<Record<string, string>>(() => ({
    dest_url: base.destination_url,
    ...metaFieldsFromCreative(base),
  }));
  const [assets, setAssets] = useState<AttachCreativeAssetRequest[]>(() =>
    base.assets.flatMap((a) =>
      a.placement_tags.map((tag) => ({
        asset_id: a.asset_id,
        placement_tag: tag,
        width: a.width,
        height: a.height,
        duration_ms: a.duration_ms,
      })),
    ),
  );
  const [files, setFiles] = useState<Record<string, LocalCreativeAsset>>({});
  // A Display file's animation, read from its bytes when it was chosen; the
  // placements already saved carry theirs on the asset.
  const [animations, setAnimations] = useState<Record<string, AssetAnimation>>({});
  // Why a chosen file was refused, beside the placement it was chosen for.
  const [refusals, setRefusals] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [discard, setDiscard] = useState(false);
  const save = useSaveCreativeContent();
  const queryClient = useQueryClient();
  const controller = useRef<AbortController | null>(null);
  const urls = useRef<string[]>([]);
  const keepEditing = useRef<HTMLButtonElement>(null);
  const reasonId = useId();
  useEffect(
    () => () => {
      controller.current?.abort();
      urls.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );
  // The prompt replaces nothing on screen, so a keyboard or screen-reader
  // user who pressed Close would otherwise stay on a button that no longer
  // does what it says. Land on the safe answer.
  useEffect(() => {
    if (discard) keepEditing.current?.focus();
  }, [discard]);
  const tags = [
    ...new Set([
      ...assets.map((a) => a.placement_tag),
      ...Object.keys(files),
      ...(base.channel === 'meta'
        ? ['meta_sq', 'meta_fs', 'meta_story']
        : base.channel === 'ctv'
          ? ['ctv_16x9']
          : []),
    ]),
  ];
  // A display slot added but not yet given a file. It is not a change — the
  // content is the same until a file is chosen — and it must never reach the
  // server, which refuses an empty asset id.
  const placeholders = assets.filter((a) => !a.asset_id).map((a) => a.placement_tag);
  const issues = creativeContentIssues(base.channel, fields);
  const hints = Object.fromEntries(issues.map((issue) => [issue.key, issue.message]));
  const reason = issues[0]?.message ?? (placeholders.length > 0 ? PLACEHOLDER_HINT : '');
  const canSave = dirty && issues.length === 0 && placeholders.length === 0;
  const close = () => {
    if (dirty) {
      setDiscard(true);
      return false;
    }
    onClose();
    return true;
  };
  async function select(tag: string, file?: File) {
    if (!file) return;
    setError('');
    setRefusals(({ [tag]: _, ...rest }) => rest);
    setBusy(true);
    try {
      let asset: LocalCreativeAsset;
      let animation: AssetAnimation | null = null;
      if (base.channel === 'dsp') {
        // Refused here, from the file's own bytes, for everything upload-init
        // and attach would refuse: the type, the 250 KB limit, a size TTD does
        // not take, or a replacement that changes the placement's size.
        const check = await validateDisplayFile(file, displayTagSize(tag));
        if (check.refusals.length > 0 || !check.image) {
          setRefusals((old) => ({ ...old, [tag]: check.refusals.join(' ') }));
          return;
        }
        asset = {
          file,
          name: file.name,
          type: 'image',
          url: URL.createObjectURL(file),
          width: check.image.width,
          height: check.image.height,
        };
        animation = check.image.animation;
      } else {
        asset = await probe(file);
      }
      urls.current.push(asset.url);
      const target = tag.startsWith(PLACEHOLDER_PREFIX)
        ? `dsp_${asset.width}x${asset.height}_${Math.max(0, ...tags.map((value) => Number(value.split('_').at(-1)) || 0)) + 1}`
        : tag;
      setAssets((old) => old.filter((a) => a.placement_tag !== tag || !!a.asset_id));
      setFiles((old) => ({ ...old, [target]: asset }));
      setAnimations(({ [target]: _, ...rest }) => (animation ? { ...rest, [target]: animation } : rest));
      setDirty(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to read file.');
    } finally {
      setBusy(false);
    }
  }
  async function submit() {
    if (!canSave) return;
    setBusy(true);
    setError('');
    setConflict(false);
    controller.current = new AbortController();
    // The uploads and the version write: a new release waits for both (2026-10-21).
    const releaseHold = holdRelease('creative_content_save');
    try {
      const next = assets.filter((a) => !!a.asset_id);
      for (const [tag, file] of Object.entries(files)) {
        const uploaded = await uploadCreativeFile({
          creativeId: base.id,
          placementTag: tag,
          asset: file,
          signal: controller.current.signal,
          onProgress: (n, total) =>
            setStage(`Uploading ${tag} · ${Math.round((n / total) * 100)}%`),
        });
        const i = next.findIndex((a) => a.placement_tag === tag);
        if (i < 0) next.push(uploaded);
        else next[i] = uploaded;
      }
      setStage('Saving version…');
      const result = await save.mutateAsync({
        id: base.id,
        body: {
          expected_revision: base.revision,
          expected_content_hash: base.content_hash ?? '',
          spec: base.channel === 'meta' ? serializeMetaCreativeSpec(fields) : base.spec,
          destination_url: (fields.dest_url ?? base.destination_url).trim(),
          assets: next,
        },
      });
      onSaved(result.creative);
      onClose();
    } catch (e) {
      // A 409 means someone else saved first. The snapshot this editor holds
      // is now behind, and so is every cached copy the host drawer reads —
      // refresh those now, so "close and reopen" loads the newer version
      // instead of the same stale one.
      if (e instanceof ApiError && e.status === 409) {
        setConflict(true);
        void queryClient.invalidateQueries({ queryKey: creativeById.key(base.id) });
        void queryClient.invalidateQueries({ queryKey: creativesList.all });
      }
      setError(e instanceof Error ? e.message : 'Unable to save. Your edits have been retained.');
    } finally {
      releaseHold();
      setBusy(false);
      setStage('');
    }
  }
  return (
    <ResponsiveOverlay
      label="Edit creative version"
      kind="form"
      className="drawer creative-content-editor"
      onClose={close}
      dismissible={!busy}
    >
      <div className="drawer-hd">
        <h2>Edit {base.name}</h2>
        <button className="btn" disabled={busy} onClick={close}>
          Close
        </button>
      </div>
      <div className="drawer-body">
        <p>
          Editing version {base.revision}. Saving creates draft changes. Campaigns keep their
          applied version until you review and explicitly apply a replacement.
        </p>
        <fieldset disabled={busy} style={{ border: 0, padding: 0 }}>
          {fieldsFor(base.channel)
            .filter((f) => f.key !== 'ad_name')
            .map((field) => (
              <CreativeFieldRow
                key={field.key}
                field={field}
                hint={hints[field.key]}
                value={fields[field.key] ?? ''}
                onChange={(key: string, value: string) => {
                  setFields((old) => ({ ...old, [key]: value }));
                  setDirty(true);
                }}
              />
            ))}
          <h3>Assets</h3>
          {tags.map((tag) => {
            const placeholder = tag.startsWith(PLACEHOLDER_PREFIX);
            const current = base.assets.find((a) => a.placement_tags.includes(tag));
            const url =
              files[tag]?.url ??
              (assets.some((a) => a.placement_tag === tag) ? current?.view_url : undefined);
            const animation = files[tag] ? animations[tag] : current?.animation;
            return (
              <div
                className="panel"
                key={tag}
                data-placement={tag}
                style={{ padding: 12, marginBottom: 12 }}
              >
                <label className="field-lbl" htmlFor={`replacement-${tag}`}>
                  {placeholder
                    ? 'New display placement'
                    : placementLabel(tag)}
                </label>
                {url &&
                  (base.media_type === 'video' ? (
                    <video src={url} controls style={{ maxWidth: '100%', maxHeight: 160 }} />
                  ) : (
                    <img src={url} alt={tag} style={{ maxWidth: '100%', maxHeight: 160 }} />
                  ))}
                <input
                  id={`replacement-${tag}`}
                  type="file"
                  accept={
                    base.media_type === 'video'
                      ? 'video/mp4,video/quicktime'
                      : base.channel === 'dsp'
                        ? DISPLAY_ACCEPT
                        : 'image/*'
                  }
                  onChange={(e) => void select(tag, e.target.files?.[0])}
                />
                {refusals[tag] && (
                  <div className="asset-err" role="alert">
                    {refusals[tag]}
                  </div>
                )}
                {base.channel === 'dsp' && (
                  <DisplayAnimationNote
                    animation={animation}
                    byteSize={files[tag] ? files[tag].file.size : current?.byte_size}
                  />
                )}
                {placeholder && (
                  <p className="muted" style={{ fontSize: 11.5, margin: '6px 0 0' }}>
                    {PLACEHOLDER_HINT}
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    // Dropping an empty slot changes nothing; dropping a real
                    // placement or a chosen file does.
                    const real =
                      !!files[tag] || assets.some((a) => a.placement_tag === tag && !!a.asset_id);
                    setAssets((old) => old.filter((a) => a.placement_tag !== tag));
                    setFiles((old) => {
                      const rest = { ...old };
                      delete rest[tag];
                      return rest;
                    });
                    setRefusals(({ [tag]: _, ...rest }) => rest);
                    if (real) setDirty(true);
                  }}
                >
                  Remove placement
                </button>
              </div>
            );
          })}
          {base.channel === 'dsp' && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                const tag = `${PLACEHOLDER_PREFIX}${crypto.randomUUID()}`;
                setAssets((old) => [...old, { asset_id: '', placement_tag: tag }]);
              }}
            >
              Add display placement
            </button>
          )}
        </fieldset>
        {error && (
          <p role="alert">
            {error} Your edits are retained.
            {conflict
              ? ' Close and reopen the editor to load the newer version, then make your changes again.'
              : ''}
          </p>
        )}
        {stage && <p role="status">{stage}</p>}
        {discard && (
          <div className="panel">
            <p>Discard unsaved changes?</p>
            <button ref={keepEditing} className="btn" onClick={() => setDiscard(false)}>
              Keep editing
            </button>
            <button className="btn" onClick={() => onClose()}>
              Discard changes
            </button>
          </div>
        )}
      </div>
      <div className="drawer-foot">
        <button
          className="btn btn-primary"
          disabled={!canSave || busy}
          aria-describedby={reason ? reasonId : undefined}
          title={reason || (dirty ? undefined : 'No changes to save')}
          onClick={() => void submit()}
        >
          Save draft version
        </button>
        {reason && (
          <span id={reasonId} className="muted" style={{ fontSize: 11.5, alignSelf: 'center' }}>
            {reason}
          </span>
        )}
        {busy && (
          <button className="btn" onClick={() => controller.current?.abort()}>
            Cancel upload
          </button>
        )}
      </div>
    </ResponsiveOverlay>
  );
}
